import React from "react";
import { Box, Option, Textarea } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import DarkSelect from "../../../common/DarkSelect";
import DarkTypography from "../../../common/DarkTypography";
import { mailEditorTextareaSx } from "../../../../styles/globalStyles";
import type { MailPlaceholderDefinition } from "api/mailTemplates/MailTemplate.typed";
import MailEditorPlaceholderChips from "./MailEditorPlaceholderChips";
import {
  MAIL_BLOCK_LABELS,
  MAIL_BODY_CONDITIONAL_CONDITION_FIELD_KEY,
  MAIL_BODY_CONDITIONAL_ELSE_FIELD_KEY,
  MAIL_BODY_CONDITIONAL_IF_FIELD_KEY,
} from "../mailBodyBlocks";
import type { MailBodyBlock } from "../mailBodyBlocks";
import {
  mailConditionFieldKeyToDisplayParts,
  mailLinePartsToConditionFieldKey,
  mergeAdjacentTextParts,
  parseHtmlToMailLineParts,
  paragraphConditionalDefault,
  sanitizeMailConditionFieldStorageKey,
  mailPhToken,
  serializeMailLinePartsToInnerHtml,
  tryParseConditionalTemplateHtml,
} from "../mailLineParts";
import { textareaOrInputPlaceholderDropProps } from "../mailEditorDnD";
import {
  mailFreeformTemplatePlaceholderChips,
  mailPlaceholderValueTypesForFreeformTemplate,
} from "../mailPlaceholderContext";
import MailConditionalBooleanChips from "./MailConditionalBooleanChips";
import MailContactBlockFields from "./MailContactBlockFields";
import MailGoogleReviewBlockFields from "./MailGoogleReviewBlockFields";
import MailUnsubscribeBlockFields from "./MailUnsubscribeBlockFields";
import MailFormatToolbar from "./MailFormatToolbar";
import MailRichLineEditor from "./MailRichLineEditor";

export type MailBodyBlockFieldsProps = {
  block: MailBodyBlock;
  canEdit: boolean;
  onPatch: (b: MailBodyBlock) => void;
  setFieldRef: (key: string, el: HTMLElement | null) => void;
  trackFocus: (blockId: string, field: string) => void;
  booleanConditionPlaceholders: MailPlaceholderDefinition[];
  presentablePhLabel: (key: string) => string;
  placeholderCatalog: MailPlaceholderDefinition[];
};

const monoTextareaSx: SxProps = [
  ...(Array.isArray(mailEditorTextareaSx) ? mailEditorTextareaSx : [mailEditorTextareaSx]),
  { fontFamily: "ui-monospace, monospace", fontSize: 12 },
];

type ParagraphBlock = Extract<MailBodyBlock, { type: "paragraph" }>;

function paragraphLayoutValue(block: ParagraphBlock): "body" | "tight" | "boxed" {
  return block.variant === "boxed" ? "boxed" : block.variant;
}

function patchParagraphLayout(
  block: ParagraphBlock,
  layout: "body" | "tight" | "boxed",
): MailBodyBlock {
  if (layout === "boxed") {
    if (block.variant === "boxed") {
      return block;
    }

    if (block.content.format === "template") {
      return {
        ...block,
        variant: "boxed",
        parts: mergeAdjacentTextParts(parseHtmlToMailLineParts(block.content.html)),
      };
    }

    if (block.content.format === "conditional") {
      return {
        ...block,
        variant: "boxed",
        parts: mergeAdjacentTextParts([...block.content.ifParts]),
      };
    }

    return { ...block, variant: "boxed", parts: block.content.parts };
  }

  if (block.variant === "boxed") {
    return {
      id: block.id,
      type: "paragraph",
      variant: layout,
      content: { format: "rich", parts: block.parts },
    };
  }

  return { ...block, variant: layout };
}

type ParagraphContentMode = "rich" | "conditional";

function paragraphContentKind(block: ParagraphBlock): ParagraphContentMode {
  if (block.variant === "boxed") {
    return "rich";
  }

  if (block.content.format === "conditional") {
    return "conditional";
  }

  return "rich";
}

function patchParagraphContentKind(
  block: ParagraphBlock,
  kind: ParagraphContentMode,
): MailBodyBlock {
  if (block.variant === "boxed") {
    return block;
  }

  if (kind === "conditional") {
    if (block.content.format === "conditional") {
      return block;
    }

    if (block.content.format === "template") {
      const tpl = block.content.html.trim();
      const parsed = tryParseConditionalTemplateHtml(tpl);

      if (parsed) {
        return { ...block, content: { format: "conditional", ...parsed } };
      }

      const nextConditional = paragraphConditionalDefault();

      return {
        ...block,
        content: {
          format: "conditional",
          conditionField: nextConditional.conditionField,
          ifParts: tpl
            ? mergeAdjacentTextParts(parseHtmlToMailLineParts(tpl))
            : [{ kind: "text" as const, text: "" }],
          elseParts: nextConditional.elseParts,
        },
      };
    }

    if (block.content.format !== "rich") {
      return block;
    }

    const richContent = block.content;
    const inner = serializeMailLinePartsToInnerHtml(richContent.parts).trim();
    const nextConditional = paragraphConditionalDefault();

    return {
      ...block,
      content: {
        format: "conditional" as const,
        conditionField: nextConditional.conditionField,
        ifParts: inner
          ? mergeAdjacentTextParts(parseHtmlToMailLineParts(inner))
          : [{ kind: "text" as const, text: "" }],
        elseParts: nextConditional.elseParts,
      },
    };
  }

  if (block.content.format === "rich") {
    return block;
  }

  if (block.content.format === "conditional") {
    return {
      ...block,
      content: {
        format: "rich",
        parts:
          block.content.ifParts.length > 0
            ? block.content.ifParts
            : [{ kind: "text" as const, text: "" }],
      },
    };
  }

  if (block.content.format !== "template") {
    return block;
  }

  const tpl = block.content.html.trim();
  const parsed = tryParseConditionalTemplateHtml(tpl);

  if (parsed) {
    return {
      ...block,
      content: { format: "rich", parts: parsed.ifParts },
    };
  }

  return {
    ...block,
    content: {
      format: "rich",
      parts: mergeAdjacentTextParts(parseHtmlToMailLineParts(tpl)),
    },
  };
}

type TextareaWithMailFormatToolbarProps = {
  value: string;
  onChange: (next: string) => void;
  minRows: number;
  disabled: boolean;
  textareaSx: SxProps;
  ariaLabel: string;
  fieldKey: string;
  blockId: string;
  focusField: string;
  setFieldRef: MailBodyBlockFieldsProps["setFieldRef"];
  trackFocus: MailBodyBlockFieldsProps["trackFocus"];
  canEdit: boolean;
  placeholder?: string;
  renderBelowToolbar?: (api: { insertAtCursor: (snippet: string) => void }) => React.ReactNode;
  placeholderCatalog?: MailPlaceholderDefinition[];
};

function TextareaWithMailFormatToolbar(props: TextareaWithMailFormatToolbarProps) {
  const taRef = React.useRef<HTMLTextAreaElement | null>(null);
  const {
    value,
    onChange,
    minRows,
    disabled,
    textareaSx,
    ariaLabel,
    fieldKey,
    blockId,
    focusField,
    setFieldRef,
    trackFocus,
    canEdit,
    placeholder,
    renderBelowToolbar,
    placeholderCatalog: textareaPlaceholderCatalog,
  } = props;

  const textareaDropProps =
    textareaPlaceholderCatalog && textareaPlaceholderCatalog.length > 0
      ? textareaOrInputPlaceholderDropProps(value, onChange, {
          catalog: textareaPlaceholderCatalog,
          allowedValueTypes: mailPlaceholderValueTypesForFreeformTemplate(),
          allowUnknownPlaceholderKey: true,
        })
      : textareaOrInputPlaceholderDropProps(value, onChange);

  const insertAtCursor = React.useCallback(
    (snippet: string) => {
      const ta = taRef.current;

      if (!ta || disabled || !canEdit) {
        return;
      }

      const start = ta.selectionStart ?? 0;
      const end = ta.selectionEnd ?? 0;
      const next = value.slice(0, start) + snippet + value.slice(end);

      onChange(next);

      const pos = start + snippet.length;

      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(pos, pos);
      });
    },
    [canEdit, disabled, onChange, value],
  );

  return (
    <Box>
      <MailFormatToolbar
        canEdit={canEdit && !disabled}
        value={value}
        onValueChange={onChange}
        textareaRef={taRef}
      />
      {renderBelowToolbar?.({ insertAtCursor })}
      <Textarea
        minRows={minRows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        sx={textareaSx}
        placeholder={placeholder}
        slotProps={{
          textarea: {
            ...textareaDropProps,
            "aria-label": ariaLabel,
            ref: (el: HTMLTextAreaElement | null) => {
              taRef.current = el;
              setFieldRef(fieldKey, el);
            },
            onFocus: () => trackFocus(blockId, focusField),
          },
        }}
      />
    </Box>
  );
}

export default function MailBodyBlockFields(props: MailBodyBlockFieldsProps) {
  const {
    block,
    canEdit,
    onPatch,
    setFieldRef,
    trackFocus,
    booleanConditionPlaceholders,
    presentablePhLabel,
    placeholderCatalog,
  } = props;

  const freeformPlaceholderChips = React.useMemo(
    () => mailFreeformTemplatePlaceholderChips(placeholderCatalog),
    [placeholderCatalog],
  );

  const renderFreeformPlaceholderBar = React.useCallback(
    ({ insertAtCursor }: { insertAtCursor: (snippet: string) => void }) =>
      freeformPlaceholderChips.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
          <DarkTypography level="body-xs" colorVariant="muted">
            Variables (fragment HTML)
          </DarkTypography>
          <MailEditorPlaceholderChips
            placeholdersForInsert={freeformPlaceholderChips}
            labelLookupCatalog={placeholderCatalog}
            canEdit={canEdit}
            onInsertPlaceholder={(key) => insertAtCursor(mailPhToken(key))}
          />
        </Box>
      ) : null,
    [freeformPlaceholderChips, placeholderCatalog, canEdit],
  );

  React.useLayoutEffect(() => {
    if (block.type !== "paragraph") {
      return;
    }

    const pb = block as ParagraphBlock;

    if (pb.variant === "boxed") {
      return;
    }

    if (pb.content.format !== "template") {
      return;
    }

    onPatch(patchParagraphContentKind(pb, "rich"));
  }, [block, onPatch]);

  switch (block.type) {
    case "title":
      return (
        <MailRichLineEditor
          parts={block.parts}
          onChange={(parts) => onPatch({ ...block, parts })}
          canEdit={canEdit}
          blockId={block.id}
          ariaLabel={MAIL_BLOCK_LABELS.title}
          setFieldRef={setFieldRef}
          trackFocus={trackFocus}
          minRowsText={2}
          presentablePhLabel={presentablePhLabel}
          placeholderCatalog={placeholderCatalog}
        />
      );
    case "footer":
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ maxWidth: 320 }}>
            <DarkSelect
              value={block.variant}
              onChange={(_e, val) => {
                const v = val as "note" | "signature" | null;

                if (v) {
                  onPatch({ ...block, variant: v });
                }
              }}
              disabled={!canEdit}
            >
              <Option value="note">Note (encadré)</Option>
              <Option value="signature">Signature (ligne forte)</Option>
            </DarkSelect>
          </Box>
          <MailRichLineEditor
            parts={block.parts}
            onChange={(parts) => onPatch({ ...block, parts })}
            canEdit={canEdit}
            blockId={block.id}
            ariaLabel={MAIL_BLOCK_LABELS.footer}
            setFieldRef={setFieldRef}
            trackFocus={trackFocus}
            minRowsText={3}
            presentablePhLabel={presentablePhLabel}
            placeholderCatalog={placeholderCatalog}
          />
        </Box>
      );
    case "paragraph": {
      const layoutRow = (
        <Box sx={{ maxWidth: 320 }}>
          <DarkSelect
            value={paragraphLayoutValue(block)}
            onChange={(_e, val) => {
              const v = val as "body" | "tight" | "boxed" | null;

              if (v) {
                onPatch(patchParagraphLayout(block, v));
              }
            }}
            disabled={!canEdit}
          >
            <Option value="body">Corps (marge normale)</Option>
            <Option value="tight">Marge réduite</Option>
            <Option value="boxed">Encadré</Option>
          </DarkSelect>
        </Box>
      );

      const contentModeRow =
        block.variant !== "boxed" ? (
          <Box sx={{ maxWidth: 360 }}>
            <DarkSelect
              value={paragraphContentKind(block)}
              onChange={(_e, val) => {
                const k = val as ParagraphContentMode | null;

                if (k) {
                  onPatch(patchParagraphContentKind(block, k));
                }
              }}
              disabled={!canEdit}
            >
              <Option value="rich">Texte formaté</Option>
              <Option value="conditional">Si / Sinon (structuré)</Option>
            </DarkSelect>
          </Box>
        ) : null;

      if (block.variant === "boxed") {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {layoutRow}
            <MailRichLineEditor
              parts={block.parts}
              onChange={(parts) => onPatch({ ...block, parts })}
              canEdit={canEdit}
              blockId={block.id}
              ariaLabel={`${MAIL_BLOCK_LABELS.paragraph} (encadré)`}
              setFieldRef={setFieldRef}
              trackFocus={trackFocus}
              minRowsText={4}
              presentablePhLabel={presentablePhLabel}
              placeholderCatalog={placeholderCatalog}
            />
          </Box>
        );
      }

      if (block.content.format === "template") {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {layoutRow}
            {contentModeRow}
          </Box>
        );
      }

      if (block.content.format === "conditional") {
        const cond = block.content;

        const patchConditionField = (nextKey: string) => {
          const key = sanitizeMailConditionFieldStorageKey(nextKey);

          onPatch({
            ...block,
            content: {
              format: "conditional",
              conditionField: key,
              ifParts: cond.ifParts,
              elseParts: cond.elseParts,
            },
          });
        };

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {layoutRow}
            {contentModeRow}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <DarkTypography level="title-sm" colorVariant="legend">
                Si (condition vraie)
              </DarkTypography>
              <MailConditionalBooleanChips
                items={booleanConditionPlaceholders}
                selectedKey={cond.conditionField}
                onSelectKey={(key) => patchConditionField(key)}
                canEdit={canEdit}
              />
              <Box sx={{ maxWidth: 400 }}>
                <MailRichLineEditor
                  variant="conditionKey"
                  editorFieldId={MAIL_BODY_CONDITIONAL_CONDITION_FIELD_KEY}
                  parts={mailConditionFieldKeyToDisplayParts(cond.conditionField)}
                  onChange={(parts) => patchConditionField(mailLinePartsToConditionFieldKey(parts))}
                  canEdit={canEdit}
                  blockId={block.id}
                  ariaLabel="Nom du champ pour {{if .Nom}} — même surface que le texte (puces, glisser-déposer)"
                  setFieldRef={setFieldRef}
                  trackFocus={trackFocus}
                  minRowsText={1}
                  presentablePhLabel={presentablePhLabel}
                  placeholderCatalog={placeholderCatalog}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <MailRichLineEditor
                editorFieldId={MAIL_BODY_CONDITIONAL_IF_FIELD_KEY}
                parts={cond.ifParts}
                onChange={(ifParts) =>
                  onPatch({
                    ...block,
                    content: {
                      format: "conditional",
                      conditionField: cond.conditionField,
                      ifParts,
                      elseParts: cond.elseParts,
                    },
                  })
                }
                canEdit={canEdit}
                blockId={block.id}
                ariaLabel={`${MAIL_BLOCK_LABELS.paragraph} — branche si`}
                setFieldRef={setFieldRef}
                trackFocus={trackFocus}
                minRowsText={3}
                presentablePhLabel={presentablePhLabel}
                placeholderCatalog={placeholderCatalog}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <DarkTypography level="title-sm" colorVariant="legend">
                Sinon
              </DarkTypography>
              <MailRichLineEditor
                editorFieldId={MAIL_BODY_CONDITIONAL_ELSE_FIELD_KEY}
                parts={cond.elseParts}
                onChange={(elseParts) =>
                  onPatch({
                    ...block,
                    content: {
                      format: "conditional",
                      conditionField: cond.conditionField,
                      ifParts: cond.ifParts,
                      elseParts,
                    },
                  })
                }
                canEdit={canEdit}
                blockId={block.id}
                ariaLabel={`${MAIL_BLOCK_LABELS.paragraph} — branche sinon`}
                setFieldRef={setFieldRef}
                trackFocus={trackFocus}
                minRowsText={3}
                presentablePhLabel={presentablePhLabel}
                placeholderCatalog={placeholderCatalog}
              />
            </Box>
          </Box>
        );
      }

      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {layoutRow}
          {contentModeRow}
          <MailRichLineEditor
            parts={block.content.parts}
            onChange={(parts) => onPatch({ ...block, content: { format: "rich", parts } })}
            canEdit={canEdit}
            blockId={block.id}
            ariaLabel={MAIL_BLOCK_LABELS.paragraph}
            setFieldRef={setFieldRef}
            trackFocus={trackFocus}
            minRowsText={3}
            presentablePhLabel={presentablePhLabel}
            placeholderCatalog={placeholderCatalog}
          />
        </Box>
      );
    }
    case "html":
      return (
        <Box sx={{ mt: 1.25 }}>
          <TextareaWithMailFormatToolbar
            value={block.html}
            onChange={(next) => onPatch({ ...block, html: next })}
            minRows={10}
            disabled={!canEdit}
            textareaSx={monoTextareaSx}
            ariaLabel="Fragment HTML — inséré tel quel dans le corps du mail"
            fieldKey={`${block.id}:html`}
            blockId={block.id}
            focusField="html"
            setFieldRef={setFieldRef}
            trackFocus={trackFocus}
            canEdit={canEdit}
            placeholderCatalog={placeholderCatalog}
            renderBelowToolbar={renderFreeformPlaceholderBar}
          />
        </Box>
      );
    case "contact":
      return (
        <MailContactBlockFields
          block={block}
          canEdit={canEdit}
          onPatch={onPatch}
          setFieldRef={setFieldRef}
          trackFocus={trackFocus}
          presentablePhLabel={presentablePhLabel}
          placeholderCatalog={placeholderCatalog}
        />
      );
    case "googleReview":
      return (
        <MailGoogleReviewBlockFields
          block={block}
          canEdit={canEdit}
          onPatch={onPatch}
          setFieldRef={setFieldRef}
          trackFocus={trackFocus}
          presentablePhLabel={presentablePhLabel}
          placeholderCatalog={placeholderCatalog}
        />
      );
    case "unsubscribe":
      return <MailUnsubscribeBlockFields />;
    default:
      return null;
  }
}
